import type { useAgentRuntime } from '@multiplayer-app/ai-agent-react'
import { StreamChunkType, type AgentMessage } from '@multiplayer-app/ai-agent-types'

type AgentRuntime = ReturnType<typeof useAgentRuntime>

/**
 * Answering an approval-gated tool call ("Apply"/"Decline" on a proposal card)
 * resumes the SAME agent turn over the transport socket, WITHOUT posting a
 * visible user message: the backend streams the continuation into a fresh
 * assistant message the initiating client is told to treat as invisible (see
 * ChatProcessor.processApprovalPayload). This is what lets one applied fix roll
 * straight into the next proposal with no "continue" chatter.
 *
 * The SDK owns this flow for its built-in approval UI but does not expose it as a
 * hook, so we replicate its exact store lifecycle (streaming status + a pending
 * assistant placeholder, fed by Message chunks) here. Mirrors the internal
 * approval component in @multiplayer-app/ai-agent-react.
 */

/** Store surface we drive; the full AgentStore type is not publicly exported. */
interface ApprovalStoreLike {
  setChatStatus: (chatKey: string, status: 'idle' | 'sending' | 'streaming') => void
  setPendingAssistant: (chatKey: string, message?: AgentMessage) => void
  appendMessagesToChat: (chatId: string, messages: AgentMessage[]) => void
}

export interface ToolApprovalParams {
  /** Chat holding the paused turn. */
  chatId: string
  /** Assistant message id that owns the approval-gated tool call. */
  messageId: string
  /** approvalId stamped on the tool call by the backend when it paused. */
  approvalId: string
  /** true = Apply, false = Decline/Skip. */
  approved: boolean
  /** Free-text result fed back to the model (why declined / what to do next). */
  userResponse?: string
}

/** Store surface for locating the message that owns a tool call. */
interface MessagesStoreLike {
  chatState: Record<string, { messages: Array<{ id: string; toolCalls?: Array<{ id: string }> }> }>
}

/**
 * Find the assistant message id whose tool calls include `toolCallId`. Tool
 * renderers receive the call and chatId but not the parent message id, which the
 * approval payload requires — recover it from the store. Searches newest-first.
 */
export function findMessageIdForToolCall(
  runtime: AgentRuntime,
  chatId: string,
  toolCallId: string
): string | undefined {
  const state = runtime.store.getState() as unknown as MessagesStoreLike
  const messages = state.chatState[chatId]?.messages ?? []
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].toolCalls?.some((toolCall) => toolCall.id === toolCallId)) return messages[i].id
  }
  return undefined
}

/**
 * Send an approval decision for a paused tool call and stream the resumed turn
 * back into the store. Resolves once the continuation finishes (or pauses again
 * on the next approval-gated proposal).
 */
export async function sendToolApproval(
  runtime: AgentRuntime,
  { chatId, messageId, approvalId, approved, userResponse }: ToolApprovalParams
): Promise<void> {
  if (!runtime.transport?.streamMessage) {
    throw new Error('This transport cannot answer tool approvals.')
  }
  const store = runtime.store.getState() as unknown as ApprovalStoreLike
  const userId = (runtime.config as { user?: { id?: string } }).user?.id ?? 'guest'

  // Re-attach the live document session to the resumed turn. The backend derives
  // the tools' context.data.docSessionId from payload.metadata (chats.service.js),
  // and the Office read tools resolve the mirrored document from it. An approval
  // payload without metadata leaves the resumed turn with no session, so the read
  // tools report "not synced" and fall back to the stale page-context snapshot.
  // getChatMetadata() re-invokes the add-in's useChatMetadata provider, so this is
  // the CURRENT docSessionId (and document identity), not a value from send time.
  const chats = (runtime.store.getState() as unknown as { chats?: Record<string, { contextKey?: string }> }).chats
  const contextKey = chats?.[chatId]?.contextKey ?? runtime.defaultContextKey
  const metadata = runtime.getChatMetadata({ contextKey })

  // Immediate feedback: show the composer as busy and render a streaming
  // placeholder until the real continuation message arrives.
  store.setChatStatus(chatId, 'streaming')
  store.setPendingAssistant(chatId, {
    id: `pending-approval-${approvalId}`,
    chat: chatId,
    role: 'assistant' as AgentMessage['role'],
    content: '',
    reasoning: '',
    toolCalls: [],
    attachments: [],
    createdAt: new Date().toISOString()
  })

  try {
    const result = await runtime.transport.streamMessage(
      { chatId, messageId, approvalId, approved, userResponse, userId, ...(metadata ? { metadata } : {}) },
      (chunk) => {
        if (chunk.type === StreamChunkType.Message && chunk.message) {
          // The continuation streams into a NEW message id; once it appears the
          // placeholder has been superseded, so drop it.
          if (chunk.message.id !== messageId) store.setPendingAssistant(chatId, undefined)
          store.appendMessagesToChat(chatId, [chunk.message])
        }
      },
      { streamKey: chatId }
    )
    if (result) store.appendMessagesToChat(result.id, result.messages)
  } finally {
    // Clear the placeholder and release the composer. streamMessage resolves once
    // the resumed turn has finished or paused on the next proposal; either way the
    // authoritative chat status arrives over the socket and, if the turn paused
    // again, the next proposal renders its own card regardless of composer state.
    // We deliberately avoid a getChat() reconcile here — it is redundant with the
    // socket update and just adds a failing request in restricted-CORS setups.
    store.setPendingAssistant(chatId, undefined)
    store.setChatStatus(chatId, 'idle')
  }
}
